import React from "react";
import { Form, Formik, FormikProps } from "formik";
import ManagersModel, { ManagerData, managerSchema, defaultManagerData } from "../../core/api/manager/ManagersModel";
import {
  Button,
  DialogActions,
  DialogContent,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  Grid
} from "@mui/material";
import { StyledTextField } from "../common/StyledComponents";
import { translations } from "../../translations/common";

interface Props {
  formValues?: ManagerData;
  onSuccess(): void;
}

const ManagersModalForm: React.FC<Props> = (props) => {
  const closePopup = props.onSuccess;

  return (
    <Formik
      initialValues={props.formValues ?? defaultManagerData}
      onSubmit={(values, actions) => {
        ManagersModel.updateOrCreate(values)
          .then(() => closePopup())
          .catch((error) => {
            const data = error.response?.data;
            if (data?.errors) {
              data.errors.forEach((element: any) => {
                actions.setFieldError(element.field, element.defaultMessage);
              });
            }
          })
          .finally(() => {
            actions.setSubmitting(false);
          });
      }}
      validationSchema={managerSchema}
    >
      {(props: FormikProps<ManagerData>) => {
        const {
          values,
          touched,
          errors,
          handleBlur,
          handleChange,
          isSubmitting,
        } = props;
        return (
          <Form>
            <DialogContent>
              <Grid
                container
                justifyContent="space-around"
                direction="row"
                spacing={2}
              >
                <Grid size={12}>
                  <StyledTextField
                    name="username"
                    id="username"
                    label={translations.managers.form.fields.username}
                    value={values.username}
                    type="text"
                    fullWidth
                    disabled={values.id !== undefined}
                    helperText={
                      errors.username && touched.username ? errors.username : ""
                    }
                    error={errors.username && touched.username ? true : false}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                </Grid>
                <Grid size={12}>
                  <StyledTextField
                    name="email"
                    id="email"
                    label={translations.managers.form.fields.email}
                    value={values.email}
                    type="text"
                    fullWidth
                    helperText={
                      errors.email && touched.email ? errors.email : ""
                    }
                    error={errors.email && touched.email ? true : false}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                </Grid>
                <Grid size={12}>
                  <StyledTextField
                      name="auth0Email"
                      id="auth0Email"
                      label={translations.managers.form.fields.auth}
                      value={values.auth0Email}
                      type="text"
                      fullWidth
                      helperText={
                        errors.auth0Email && touched.auth0Email ? errors.auth0Email : ""
                      }
                      error={errors.auth0Email && touched.auth0Email ? true : false}
                      onChange={handleChange}
                      onBlur={handleBlur}
                  />
                </Grid>
                <Grid size={12}>
                  <FormControl fullWidth>
                    <InputLabel id="user-type-label" sx={{ pl: 1 }}>
                      {translations.managers.form.fields.userType}
                    </InputLabel>
                    <Select
                      name="userType"
                      labelId="user-type-label"
                      id="user-type-select"
                      value={values.userType}
                      label={translations.managers.form.fields.userType}
                      error={errors.userType && touched.userType ? true : false}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    >
                      <MenuItem value={"ADMIN"}>{translations.managers.userTypes.ADMIN}</MenuItem>
                      <MenuItem value={"USER"}>{translations.managers.userTypes.USER}</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid size={12}>
                  <StyledTextField
                    name="firstName"
                    id="firstName"
                    label={translations.managers.form.fields.firstName}
                    value={values.firstName}
                    type="text"
                    fullWidth
                    helperText={
                      errors.firstName && touched.firstName
                        ? errors.firstName
                        : ""
                    }
                    error={errors.firstName && touched.firstName ? true : false}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                </Grid>
                <Grid size={12}>
                  <StyledTextField
                    name="lastName"
                    id="lastName"
                    label={translations.managers.form.fields.lastName}
                    value={values.lastName}
                    type="text"
                    fullWidth
                    helperText={
                      errors.lastName && touched.lastName ? errors.lastName : ""
                    }
                    error={errors.lastName && touched.lastName ? true : false}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                </Grid>
                <Grid size={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={values.active}
                        onChange={handleChange}
                        name="active"
                      />
                    }
                    label={translations.managers.form.fields.active}
                  />
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button
                type="button"
                className="outline"
                onClick={closePopup}
                disabled={isSubmitting}
              >
                {translations.managers.form.buttons.cancel}
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting}
                variant="contained"
                color="primary"
              >
                {translations.managers.form.buttons.save}
              </Button>
            </DialogActions>
          </Form>
        );
      }}
    </Formik>
  );
};

export default ManagersModalForm;
